// SPDX-License-Identifier: AGPL-3.0-or-later
import { occurrences, type IsoDate } from "@almanac/domain";
import type { CalendarEvent, FeedPrefs, PushDetail } from "./model.js";

/** One entry in the published calendar feed. Mirrors the server's FeedEntry. */
export interface FeedEntry {
  uid: string;
  date: IsoDate;
  title: string;
  repeat: CalendarEvent["repeat"];
  until: IsoDate | null;
}

export { occurrences };

/** One entry in the phone reminder schedule. Mirrors the server's PushEntry. */
export interface PushEntry {
  uid: string;
  date: IsoDate;
  repeat: CalendarEvent["repeat"];
  until: IsoDate | null;
  /** Null unless the user allowed titles in pushes. */
  label: string | null;
}

export interface Occurrence {
  event: CalendarEvent;
  date: IsoDate;
}

/** Every occurrence of every event in a date range, in date order. */
export function agenda(events: readonly CalendarEvent[], from: IsoDate, to: IsoDate): Occurrence[] {
  return events
    .flatMap((event) => occurrences(event, from, to).map((date) => ({ event, date })))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.event.title.localeCompare(b.event.title)));
}

/**
 * What the browser publishes to the server for the calendar feed: only
 * events not marked hidden, and only the title unless the user chose to
 * include amounts. Notes and categories never leave the browser.
 */
export function feedEntries(events: readonly CalendarEvent[], prefs: FeedPrefs, money: (cents: number) => string): FeedEntry[] {
  return events
    .filter((e) => !e.hidden)
    .map((e) => ({
      uid: e.id,
      date: e.date,
      title: (prefs.detail === "amount" && e.amount ? `${e.title} (${money(e.amount)})` : e.title).slice(0, 200),
      repeat: e.repeat,
      until: e.until,
    }));
}

/**
 * The reminder schedule sent to the server for phone pushes. Events kept out
 * of the feed stay out of this too. Titles (and amounts) are included only
 * when the user allowed them; otherwise the push just counts reminders.
 */
export function pushEntries(events: readonly CalendarEvent[], detail: PushDetail, money: (cents: number) => string): PushEntry[] {
  return events
    .filter((e) => !e.hidden)
    .map((e) => ({
      uid: e.id,
      date: e.date,
      repeat: e.repeat,
      until: e.until,
      label: detail === "generic" ? null : (detail === "amounts" && e.amount ? `${e.title} (${money(e.amount)})` : e.title).slice(0, 120),
    }));
}
